import { yupResolver } from '@hookform/resolvers/yup';
import { TFunction } from 'i18next';
import { FC, memo, useCallback } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from 'shared/components/Button/Button';
import { mergeClasses } from 'shared/utils/mergeClasses';
import * as yup from 'yup';
import { OTPInput } from '../components/subcomponents/OtpInput';
import { ResendButton } from '../components/subcomponents/ResendButton';

export interface OtpStepInputs {
  otp: string;
}

interface OtpStepProps {
  onOtpSubmit: (otp: string) => Promise<string>;
  onOtpResend: () => Promise<string>;
  onSubmitSuccess?: () => void;
  loading?: boolean;
  title: string;
  subtitle: string;
  centerTitle?: boolean;
  fullWidth?: boolean;
  className?: string;
}

export const otpInputStepSchema = (t: TFunction) =>
  yup.object({
    otp: yup
      .string()
      .transform((value) => value.trim())
      .length(6, t('auth:validationMessages.otpLength') ?? '')
      .required(t('auth:validationMessages.fieldRequired') ?? ''),
  });

export const OtpStep: FC<OtpStepProps> = memo(
  ({
    onOtpSubmit,
    onOtpResend,
    onSubmitSuccess,
    loading,
    title,
    subtitle,
    centerTitle = false,
    fullWidth = false,
    className = '',
  }) => {
    const { t } = useTranslation();
    const { handleSubmit, setError, control } = useForm<OtpStepInputs>({
      defaultValues: {
        otp: '',
      },
      resolver: yupResolver(otpInputStepSchema(t)),
    });

    const onSubmit: SubmitHandler<OtpStepInputs> = useCallback(
      async ({ otp }) => {
        try {
          const res = await onOtpSubmit(otp);
          if (res !== 'success') {
            setError('otp', {
              message: t('auth:incorrectOtp'),
            });
          }
          if (onSubmitSuccess) {
            onSubmitSuccess();
          }
        } catch (err) {
          if (err instanceof Error) {
            setError('otp', {
              message: err.message,
            });
            return;
          }
          setError('otp', {
            message: t('auth:validationMessages.unknownError'),
          });
        }
      },
      [onOtpSubmit, setError],
    );

    return (
      <form
        className={mergeClasses(
          'flex min-w-[343px] max-w-full flex-col gap-6 rounded-2xl p-6 lg:min-w-[400px] lg:max-w-[420px]',
          fullWidth ? 'w-full' : 'lg:w-[27vw]',
          className,
        )}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-2">
          <h2
            className={mergeClasses(
              'whitespace-pre-line text-left text-2xl font-bold',
              centerTitle ? 'text-center' : '',
            )}
          >
            {title}
          </h2>
          <h3 className="text-left text-sm text-gray">{subtitle}</h3>
        </div>
        <div className="flex w-full justify-center">
          <Controller
            render={({ field, fieldState: { error } }) => (
              <OTPInput
                value={field.value}
                length={6}
                onChange={field.onChange}
                error={error}
                isTranslucent={false}
                disabled={loading}
                className="lg:max-w-[500px]"
              />
            )}
            name="otp"
            control={control}
          />{' '}
        </div>
        <Button
          name="verifyOtpButton"
          loading={loading}
          disabled={loading}
          size="md"
          type="submit"
          variant="contained"
          color="primary"
        >
          {t('action:next')}
        </Button>
        <div className="text-gray-500">
          <ResendButton onOtpResend={onOtpResend} loading={loading} color="primary" />
        </div>
      </form>
    );
  },
);
